<template>
  <div class="relative">
    <client-only>
      <swiper
        v-if="relatedArticles.length > 0"
        ref="swiper"
        :modules="modules"
        :slides-per-view="slidesPerView"
        :space-between="spaceBetween"
        :centered-slides="centeredSlides"
        :loop="loopEnabled"
        :navigation="true"
        :style="{}"
        class=""
      >
        <swiper-slide v-for="article in relatedArticles" :key="article.sys.id">
          <content-card :article="article"></content-card>
        </swiper-slide>
      </swiper>
    </client-only>
  </div>
</template>

<script setup lang="ts">
import 'swiper/css'
import 'swiper/css/navigation'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { Article } from '../types/contentful'

interface Props {
  categoryId?: string
  portalId?: string
  excludeSlug?: string
  sliderType?: 'djurfakta' | 'category' | 'recipe' | 'default'
}

const props = withDefaults(defineProps<Props>(), {
  categoryId: undefined,
  portalId: undefined,
  excludeSlug: undefined,
  sliderType: 'default',
})

const { getArticles } = useContentful()

const relatedArticles = await useStatic(
  `related-articles:${props.categoryId || props.portalId || 'default'}:${props.excludeSlug || 'none'}`,
  async () => {
    const query: any = {
      order: 'fields.slider,-fields.title', // Prioritize slider: true first, then by title
      limit: 20, // Increased limit to have more articles to filter from
      include: 2,
    }

    // If we have a category ID, get articles from that category
    if (props.categoryId) {
      query['fields.category.sys.id'] = props.categoryId
    }
    // If we have a portal ID, get articles from that portal
    else if (props.portalId) {
      query['fields.parent.sys.id'] = props.portalId
    }

    const response = await getArticles(query)
    // Filter articles based on slider field and exclude current article
    const filteredArticles = response.items.filter((article: Article) => {
      // Exclude current article if we have an exclude slug
      if (props.excludeSlug && article.fields.slug === props.excludeSlug) {
        return false
      }

      // Exclude articles with slider set to false (Nej)
      if (article.fields.slider === false) {
        return false
      }

      return true
    })

    // Sort articles: prioritized first (slider: true), then random order for others
    const prioritizedArticles = filteredArticles.filter(
      (article: Article) => article.fields.slider === true,
    )
    const otherArticles = filteredArticles.filter(
      (article: Article) => article.fields.slider !== true,
    )

    // Shuffle other articles for random order
    const shuffledOtherArticles = otherArticles.sort(() => Math.random() - 0.5)

    // Combine prioritized articles first, then shuffled others, limit to 10
    const sortedArticles = [...prioritizedArticles, ...shuffledOtherArticles].slice(0, 10)

    // Attach includes data to each article for proper asset resolution
    const articlesWithIncludes = sortedArticles.map((article: Article) => ({
      ...article,
      includes: response.includes,
    }))

    return articlesWithIncludes
  },
)

const modules = ref([Navigation])

// Slider configuration based on type
const slidesPerView = computed(() => {
  switch (props.sliderType) {
    case 'djurfakta':
      return 1 // Show only 1 at a time for djurfakta
    case 'category':
    case 'recipe':
    case 'default':
    default:
      return 1 // Default to 1 for all types
  }
})

const spaceBetween = computed(() => {
  switch (props.sliderType) {
    case 'djurfakta':
      return 8
    case 'category':
    case 'recipe':
    case 'default':
    default:
      return 8
  }
})

const centeredSlides = computed(() => {
  switch (props.sliderType) {
    case 'djurfakta':
      return true
    case 'category':
    case 'recipe':
    case 'default':
    default:
      return true
  }
})

const loopEnabled = computed(() => {
  switch (props.sliderType) {
    case 'djurfakta':
      return true
    case 'category':
    case 'recipe':
    case 'default':
    default:
      return true
  }
})
</script>
